import { useTranslations } from '@aikoa/i18n/react'
import { useMemo, useState } from 'react'
import { View } from 'react-native'
import { Detail } from '~/components/typography/Content'
import {
  Collapsible,
  CollapsibleContent,
  CollapsiblePreview,
  CollapsibleTrigger,
} from '~/components/ui/collapsible'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

export function InterestDescription({
  description,
  className,
  textClassName,
  labelClassName,
  previewLength = 32,
  expandedLabel,
  defaultExpanded = true,
  showLabelForShort = true,
}: {
  description: string
  className?: string
  textClassName?: string
  labelClassName?: string
  previewLength?: number
  expandedLabel?: string
  defaultExpanded?: boolean
  showLabelForShort?: boolean
}) {
  const { t } = useTranslations('interest')
  const defaultExpandedLabel = expandedLabel ?? t(($) => $.description.expandedLabel)

  const normalized = useMemo(() => String(description ?? '').trim(), [description])

  const isLong = normalized.length > previewLength + previewLength / 3

  const preview = useMemo(() => {
    if (!isLong) {
      return normalized
    }

    const slice = normalized.slice(0, previewLength).trimEnd()
    return `${slice}…`
  }, [normalized, isLong, previewLength])

  const [isOpen, setIsOpen] = useState(defaultExpanded)

  if (!normalized) {
    return null
  }

  if (!isLong) {
    return (
      <View className={cn(className)}>
        {showLabelForShort ? (
          <Detail className={cn('text-left text-xs uppercase', labelClassName)}>
            {defaultExpandedLabel}
          </Detail>
        ) : null}
        <Text
          className={cn(
            'flex-1 text-pretty text-left text-xl leading-relaxed',
            textClassName,
            className
          )}>
          {normalized}
        </Text>
      </View>
    )
  }

  return (
    <Collapsible isOpen={isOpen} onOpenChange={setIsOpen} className={cn('w-full', className)}>
      <CollapsibleTrigger
        className={cn('mb-2 flex-wrap items-start self-start')}
        labelClassName={cn('flex-1', labelClassName)}>
        {defaultExpandedLabel}
      </CollapsibleTrigger>
      <CollapsiblePreview duration={150}>
        <Text className={cn('text-pretty text-left text-lg leading-relaxed', textClassName)}>
          {preview}
        </Text>
      </CollapsiblePreview>
      <CollapsibleContent duration={150}>
        <Text className={cn('text-pretty text-left text-lg leading-relaxed', textClassName)}>
          {normalized}
        </Text>
      </CollapsibleContent>
    </Collapsible>
  )
}
